"use client";

import { cn } from "@ui/lib";
import { AlertCircleIcon, CheckIcon, StarIcon } from "lucide-react";
import { useTranslations } from "next-intl";

export type CurrentPlanBannerKind =
	| "legacy"
	| "current"
	| "trial"
	| "trialEnded";

/**
 * In-app only: the plan the organization is on, above the v2 plan cards.
 * For a customer on legacy pricing the message is reassurance first (your
 * price stays), then the reason to look further down (more prompts, agency
 * tools). Trials get a nudge to subscribe before or after they end.
 */
export function CurrentPlanBanner({
	kind,
	planTitle,
	summary,
	className,
}: {
	kind: CurrentPlanBannerKind;
	planTitle: string;
	/** e.g. "$29/mo · 25 prompts tracked daily". */
	summary?: string;
	className?: string;
}) {
	const t = useTranslations();
	const eyebrow =
		kind === "trial"
			? t("pricing.v2.trial.eyebrow")
			: kind === "trialEnded"
				? t("pricing.v2.trialEnded.eyebrow")
				: t("pricing.v2.yourPlan");
	const body =
		kind === "legacy"
			? t("pricing.v2.legacy.body")
			: kind === "trial"
				? t("pricing.v2.trial.body")
				: kind === "trialEnded"
					? t("pricing.v2.trialEnded.body")
					: null;
	const Icon =
		kind === "trial"
			? StarIcon
			: kind === "trialEnded"
				? AlertCircleIcon
				: CheckIcon;

	return (
		<div
			className={cn(
				"flex flex-col gap-4 rounded-2xl border-2 p-5 sm:flex-row sm:items-center sm:justify-between",
				kind === "trialEnded"
					? "border-highlight/50 bg-highlight/10"
					: "border-primary/50 bg-primary/5",
				className,
			)}
		>
			<div className="flex items-start gap-3">
				<span
					className={cn(
						"flex size-8 shrink-0 items-center justify-center rounded-full",
						kind === "trialEnded"
							? "bg-highlight text-highlight-foreground"
							: "bg-primary text-primary-foreground",
					)}
				>
					<Icon className="size-4" aria-hidden />
				</span>
				<div>
					<p
						className={cn(
							"text-xs font-bold uppercase tracking-wide",
							kind === "trialEnded"
								? "text-foreground"
								: "text-primary",
						)}
					>
						{eyebrow}
					</p>
					<p className="mt-0.5 text-lg font-bold tracking-tight">
						{planTitle}
					</p>
					{summary ? (
						<p className="text-sm text-muted-foreground">
							{summary}
						</p>
					) : null}
				</div>
			</div>
			{body ? (
				<p className="max-w-sm text-sm text-muted-foreground sm:text-right">
					{body}
				</p>
			) : null}
		</div>
	);
}
